/**
 * useSports Hook
 * React hook for fetching and using sports data
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getSports, getFallbackSports, getSportEmojiSync, getPositionsSync, getGameFormatsSync } from '../api/sports';

/**
 * Hook to fetch and manage sports data
 * @param {boolean} autoFetch - Whether to fetch sports on mount (default: true)
 * @returns {Object} Sports data and helper functions
 */
export const useSports = (autoFetch = true) => {
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSports = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSports(forceRefresh);
      setSports(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch sports');
      // Use fallback data on error
      setSports(getFallbackSports());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchSports();
    }
  }, [autoFetch, fetchSports]);

  const refresh = useCallback(async () => {
    await fetchSports(true);
  }, [fetchSports]);

  const getSportByName = useCallback((name) => {
    return sports.find(s => s.name.toLowerCase() === name?.toLowerCase());
  }, [sports]);

  const getPositions = useCallback((sportName) => {
    const sport = getSportByName(sportName);
    return sport?.positions || getPositionsSync(sportName);
  }, [getSportByName]);

  const getGameFormats = useCallback((sportName) => {
    const sport = getSportByName(sportName);
    return sport?.gameFormats || getGameFormatsSync(sportName);
  }, [getSportByName]);

  const getEmoji = useCallback((sportName) => {
    const sport = getSportByName(sportName);
    return sport?.emoji || getSportEmojiSync(sportName);
  }, [getSportByName]);

  const getIcon = useCallback((sportName) => {
    const sport = getSportByName(sportName);
    return sport?.icon || 'trophy';
  }, [getSportByName]);

  // Computed values
  const sportNames = useMemo(() => sports.map(s => s.name), [sports]);
  
  // For dropdowns with value/label format
  const sportsOptions = useMemo(() => 
    sports.map(s => ({ value: s.name, label: s.name, emoji: s.emoji })),
    [sports]
  );

  // With "All" option for filters
  const sportsOptionsWithAll = useMemo(() => [
    { value: 'All', label: 'All Sports', emoji: '🏆' },
    ...sportsOptions
  ], [sportsOptions]);

  return {
    sports,
    sportNames,
    sportsOptions,
    sportsOptionsWithAll,
    loading,
    error,
    refresh,
    getSportByName,
    getPositions,
    getGameFormats,
    getEmoji,
    getIcon,
  };
};

export default useSports;


